// src/components/Map/RouteSimulator.tsx
// VERSION FINALE ULTIME – TOUT MARCHE PARFAITEMENT

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Marker, useMap } from "react-leaflet";
import L, { DivIcon, LatLngExpression } from "leaflet";

interface RouteSimulatorProps {
  path: [number, number][];
  speed?: number;
  playing?: boolean;
  icon?: DivIcon;
  autofit?: boolean;
  right?: boolean;
  iconSize?: number;
}

const RouteSimulator: React.FC<RouteSimulatorProps> = ({
  path,
  speed = 100,
  playing = true,
  icon,
  autofit = false,
  right = true,
  iconSize = 42,
}) => {
  const map = useMap();
  const [index, setIndex] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isPlaying = useRef(playing);

  // Icône rouge pulsante magnifique
  const pulseIcon = useMemo(() => {
    const html = `
      <div style="position:relative;width:${iconSize}px;height:${iconSize}px;">
        <div style="
          background:#ff3b30;
          border:4px solid #ff6b6b;
          border-radius:50%;
          width:100%;height:100%;
          box-shadow:0 0 30px #ff3b30;
          animation:pulse 1.8s infinite;
        "></div>
        <div style="
          position:absolute;
          inset:-14px;
          border:4px solid #ff3b30;
          border-radius:50%;
          animation:ripple 1.8s infinite;
          opacity:0;
        "></div>
      </div>
      <style>
        @keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.18)}}
        @keyframes ripple{0%{transform:scale(0.7);opacity:0.8}100%{transform:scale(3);opacity:0}}
      </style>
    `;

    return L.divIcon({
      html,
      className: "route-simulator-pulse",
      iconSize: [iconSize, iconSize],
      iconAnchor: [iconSize / 2, iconSize / 2],
    });
  }, [iconSize]);

  const effectiveIcon = icon ?? pulseIcon;

  // Contrôles
  const stop = () => {
    isPlaying.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const start = () => {
    if (path.length < 2 || isPlaying.current) return;
    isPlaying.current = true;
    const interval = Math.max(30, 1000 / (speed / 8));
    timerRef.current = setInterval(() => {
      setIndex((i) => {
        if (i + 1 >= path.length) {
          stop();
          return path.length - 1;
        }
        return i + 1;
      });
    }, interval);
  };

  const reset = () => {
    stop();
    setIndex(0);
    if (autofit && path.length > 1) {
      map.fitBounds(L.polyline(path).getBounds(), { padding: [60, 60] });
    }
  };

  // Play/Pause externe
  useEffect(() => {
    playing ? start() : stop();
  }, [playing]);

  // Reset quand le path change
  useEffect(() => {
    reset();
  }, [path]);

  // MANIPULATION À LA ROULETTE (LE FEU PUR)
  useEffect(() => {
    if (path.length === 0) return;

    const container = map.getContainer();

    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();
      stop(); // pause auto dès que tu touches

      setIndex((i) => {
        const step = e.deltaY > 0 ? 8 : -8; // rapide et fluide
        const next = i + step;
        if (next < 0) return 0;
        if (next >= path.length) return path.length - 1;
        return next;
      });
    };

    container.addEventListener("wheel", onWheel, { passive: false });

    // Désactiver le zoom quand Shift enfoncé
    const disableZoom = () => map.scrollWheelZoom.disable();
    const enableZoom = () => map.scrollWheelZoom.enable();

    window.addEventListener("keydown", (e) => e.key === "Shift" && disableZoom());
    window.addEventListener("keyup", (e) => e.key === "Shift" && enableZoom());

    return () => {
      container.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", disableZoom);
      window.removeEventListener("keyup", enableZoom);
      map.scrollWheelZoom.enable();
    };
  }, [map, path.length]);

  // Nettoyage final
    // Nettoyage parfait – zéro warning, zéro erreur
  useEffect(() => {
    return () => {
      timerRef.current && clearInterval(timerRef.current);
    };
  }, []);
  if (path.length === 0) return null;

  return (
    <>
      {/* Marqueur animé */}
      <Marker position={path[index]} icon={effectiveIcon} zIndexOffset={2000} />

      {/* Contrôleur moderne + indication molette */}
      <div
        style={{
          position: "absolute",
          top: 12,
          [right ? "right" : "left"]: right ? 70 : 12,
          zIndex: 1000,
          background: "rgba(0,0,0,0.82)",
          color: "#fff",
          padding: "10px 16px",
          borderRadius: "14px",
          fontSize: "13px",
          fontWeight: "600",
          backdropFilter: "blur(12px)",
          boxShadow: "0 6px 25px rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div style={{ opacity: 0.9 }}>
          <i className="fas fa-hand-pointer"></i> Shift + molette = contrôle
        </div>

        <div style={{ borderLeft: "1px solid rgba(255,255,255,0.3)", paddingLeft: "12px" }}>
          <i className="fas fa-tachometer-alt"></i> {speed} km/h
          <span className="mx-2">•</span>
          {index + 1} / {path.length}
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => (isPlaying.current ? stop() : start())}
            style={{
              background: isPlaying.current ? "#ff3b30" : "#34c759",
              color: "white",
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "17px",
            }}
          >
            <i className={`fas fa-${isPlaying.current ? "pause" : "play"}`}></i>
          </button>

          <button
            onClick={reset}
            style={{
              background: "#636366",
              color: "white",
              border: "none",
              width: 38,
              height: 38,
              borderRadius: "50%",
              cursor: "pointer",
            }}
          >
            <i className="fas fa-redo"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default RouteSimulator;